import React from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Button,
  Badge,
  Collapse,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { SidebarItem } from './SidebarItem';
import type { SidebarNode } from './navigationDefinitions';

const CURRENT_ASSIGNMENT_LABEL_KEY = 'common.currentAssignment';

function ThirdLevel({
  node,
  onNavigate,
  t,
}: {
  node: SidebarNode;
  onNavigate: (path: string) => void;
  t: (k: string) => string;
}): JSX.Element {
  return (
    <VStack align="stretch" pl={6} mt={1} spacing={1}>
      {node.children!.map((gc) => (
        <Button
          key={gc.id}
          onClick={() => gc.to && onNavigate(gc.to)}
          variant="ghost"
          px={2}
          py={2}
          borderRadius="md"
          borderLeft={gc.isCurrent ? '3px solid' : undefined}
          borderLeftColor={gc.isCurrent ? 'blue.500' : undefined}
          bg={gc.isCurrent ? 'blue.50' : gc.isCompleted ? 'green.50' : undefined}
          _hover={{
            bg: gc.isCurrent ? 'blue.100' : gc.isCompleted ? 'green.100' : 'gray.50',
          }}
          display="flex"
          alignItems="center"
          gap={2}
          justifyContent="flex-start"
          w="full"
          fontWeight={gc.isCurrent ? 'bold' : 'normal'}
          fontSize={gc.isCurrent ? 'md' : 'sm'}
        >
          <Box as={gc.icon} boxSize={gc.isCurrent ? 4 : 3} />
          <Text isTruncated>{gc.label}</Text>
          {gc.isCurrent && (
            <Badge colorScheme="blue" fontSize="10px" ml="auto">
              {t(CURRENT_ASSIGNMENT_LABEL_KEY)}
            </Badge>
          )}
        </Button>
      ))}
    </VStack>
  );
}

function CollapsedNode({
  node,
  onNavigate,
}: {
  node: SidebarNode;
  onNavigate: (path: string) => void;
}): JSX.Element {
  return (
    <Popover placement="right-start" trigger="hover" openDelay={150} closeDelay={150}>
      <PopoverTrigger>
        <Box>
          <SidebarItem to={node.to} onNavigate={onNavigate} label={node.label} icon={node.icon} collapsed />
        </Box>
      </PopoverTrigger>
      <PopoverContent w="240px">
        <PopoverBody>
          <VStack align="stretch" spacing={1}>
            {node.children?.map((c) =>
              c.children ? (
                <Box key={c.id}>
                  <HStack
                    px={2}
                    py={1}
                    borderRadius="md"
                    _hover={{ bg: 'gray.50' }}
                    cursor="pointer"
                    onClick={() => c.to && onNavigate(c.to)}
                    justifyContent="space-between"
                    mb={1}
                  >
                    <HStack gap={2}>
                      <Box as={c.icon} boxSize={3} />
                      <Text fontSize="sm" fontWeight="medium">
                        {c.label}
                      </Text>
                    </HStack>
                  </HStack>
                  <VStack align="stretch" pl={2} spacing={1}>
                    {c.children.map((gc) => (
                      <SidebarItem
                        key={gc.id}
                        to={gc.to}
                        onNavigate={onNavigate}
                        label={gc.label}
                        icon={gc.icon}
                        collapsed={false}
                      />
                    ))}
                  </VStack>
                </Box>
              ) : (
                <SidebarItem key={c.id} to={c.to} onNavigate={onNavigate} label={c.label} icon={c.icon} collapsed={false} />
              ),
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

function ExpandedNode({
  node,
  onNavigate,
  openMap,
  toggle,
  normalizedQuery,
  t,
}: {
  node: SidebarNode;
  onNavigate: (path: string) => void;
  openMap: Record<string, boolean>;
  toggle: (id: string) => void;
  normalizedQuery: string;
  t: (k: string) => string;
}): JSX.Element {
  return (
    <Box>
      <HStack
        px={2}
        py={2}
        borderRadius="md"
        _hover={{ bg: 'gray.50' }}
        cursor="pointer"
        onClick={() => node.to && onNavigate(node.to)}
        justifyContent="space-between"
      >
        <HStack gap={3}>
          <Box as={node.icon} />
          <Text>{node.label}</Text>
        </HStack>
        {node.children && (
          <ChevronDownIcon
            transform={openMap[node.id] || normalizedQuery ? 'rotate(180deg)' : 'rotate(0)'}
            onClick={(e) => {
              e.stopPropagation();
              toggle(node.id);
            }}
          />
        )}
      </HStack>
      {node.children && (
        <Collapse in={!!openMap[node.id] || !!normalizedQuery} animateOpacity={false}>
          <VStack align="stretch" pl={6} mt={1} spacing={1}>
            {node.children.map((c) =>
              c.children ? (
                <Box key={c.id}>
                  <HStack
                    px={2}
                    py={2}
                    borderRadius="md"
                    _hover={{ bg: 'gray.50' }}
                    cursor="pointer"
                    onClick={() => c.to && onNavigate(c.to)}
                    justifyContent="space-between"
                  >
                    <HStack gap={3}>
                      <Box as={c.icon} />
                      <Text fontSize="sm">{c.label}</Text>
                    </HStack>
                    <ChevronDownIcon
                      transform={openMap[c.id] || normalizedQuery ? 'rotate(180deg)' : 'rotate(0)'}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(c.id);
                      }}
                    />
                  </HStack>
                  <Collapse in={!!openMap[c.id] || !!normalizedQuery} animateOpacity={false}>
                    <ThirdLevel node={c} onNavigate={onNavigate} t={t} />
                  </Collapse>
                </Box>
              ) : (
                <SidebarItem key={c.id} to={c.to} onNavigate={onNavigate} label={c.label} icon={c.icon} collapsed={false} />
              ),
            )}
          </VStack>
        </Collapse>
      )}
    </Box>
  );
}

export default function SidebarTreeItem({
  node,
  collapsed,
  onNavigate,
  openMap,
  toggle,
  normalizedQuery,
  t,
}: {
  node: SidebarNode;
  collapsed: boolean;
  onNavigate: (path: string) => void;
  openMap: Record<string, boolean>;
  toggle: (id: string) => void;
  normalizedQuery: string;
  t: (k: string) => string;
}): JSX.Element {
  if (collapsed) {
    return <CollapsedNode node={node} onNavigate={onNavigate} />;
  }
  return (
    <ExpandedNode
      node={node}
      onNavigate={onNavigate}
      openMap={openMap}
      toggle={toggle}
      normalizedQuery={normalizedQuery}
      t={t}
    />
  );
}
